import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Bar,
  BarChart,
  Brush,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  NameType,
  Payload,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

import { TooltipProps } from 'recharts/types/component/Tooltip';

import { IconOffersPrimary, Modal } from '../../../../../component';
import { List } from '../../../../../component/list/list';
import { colors } from '../../../../../shared/constants';
import { modalOpen } from '../../../redux/dashboardActions';
import {
  BuildingDetailsPrimary,
  BuildingDetailsPrimaryTableStructureHeader,
  ChartKeys,
  EnterpriseAnalysis,
  EnterpriseSummary,
  EnterpriseSummaryKeys,
} from '../../../types';

const BuildingDetailsModalOffers: React.FC = () => {
  const dispatch = useDispatch();

  const { preference } = useSelector((state: any) => state.layoutState);
  const nls = require(`../../nls/${preference.language.id}.json`);
  const { statusModal } = useSelector((state: any) => state.dashboardState);
  const buildingData: BuildingDetailsPrimary = useSelector(
    (state: any) => state.offersPrimaryState.buildingData
  );
  const currencyMask = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const id = 'realtyOffers';

  const structureHeader: BuildingDetailsPrimaryTableStructureHeader = {
    0: ['address', 'real_state_developers', 'sales_company'],
    1: [
      'real_state_developers',
      'sales_company',
      'usage_type',
      'status',
      'launched_at',
      'construction_pattern',
      'terrain_area',
      'number_of_towers',
      'number_of_pavements',
      'number_of_units_per_pavement',
      'number_of_elevators',
      'list_of_garages',
      'private_area',
      'number_of_units',
      'percentual_of_units_sold',
      'units_available',
      'vgv',
      'city_zone_value',
    ],
  };

  const tableHeader = [
    {
      text: nls.caracteristicas,
      column: 'features',
      className: 'text-center',
    },
    {
      text: nls.type,
      column: 'type',
      className: 'text-center',
    },
    {
      text: nls.total_de_unidades,
      column: 'units_per_area',
      className: 'text-center',
    },
    {
      text: nls.percentual_of_units_sold,
      column: 'percentage_sold-custom',
      className: 'text-center',
    },
    {
      text: nls.unitsInStock,
      column: 'units_available_per_area-custom',
      className: 'text-center',
    },
    {
      text: nls.unitPriceAndSqMtPriceOnLaunch,
      column: 'unit_price_and_sqr_meter_price_on_release',
      className: 'text-center',
    },
    {
      text: nls.unitPriceAndSqMtPriceOn2021,
      column: 'unit_price_and_sqr_meter_price_on_research',
      className: 'text-center',
    },
    {
      text: nls.dateTable,
      column: 'table_date',
      className: 'text-center',
    },
    {
      text: nls.unitPriceAndSqMtPriceOnClose,
      column: 'unit_price_and_sqr_meter_price_on_closing',
      className: 'text-center',
    },
    {
      text: nls.dateClose,
      column: 'closing_price_date',
      className: 'text-center',
    },
  ];

  const { enterprise_analysis } = buildingData;
  const chartListype: ChartKeys[] = [
    'square_meter_price_over_period',
    'sold_units_over_price',
    'releases_sold_and_on_stock_over_period',
  ];
  const getFormattedTableData = (
    enterpriseAnalysisArray: EnterpriseAnalysis[]
  ): EnterpriseAnalysis[] => {
    const removeDay = (date: string) => {
      const [day, ...rest] = date.split('/');
      return rest.join('/');
    };
    const formattedValue = enterpriseAnalysisArray.map(
      (enterpriseAnalysis) => ({
        ...enterpriseAnalysis,
        table_date: enterpriseAnalysis.table_date
          ? removeDay(enterpriseAnalysis.table_date)
          : null,
        closing_price_date: enterpriseAnalysis.closing_price_date
          ? removeDay(enterpriseAnalysis.closing_price_date)
          : null,
      })
    );
    return formattedValue;
  };
  const [formattedTableData, setFormattedTableData] = useState<
    EnterpriseAnalysis[]
  >(getFormattedTableData(enterprise_analysis));

  useEffect(() => {
    setFormattedTableData(getFormattedTableData(enterprise_analysis));
  }, [buildingData]);

  const graficos = (chartKey: ChartKeys) => {
    let formatter: (value: string | number) => string,
      tooltipContent1: ({
        active,
        payload,
        label,
      }: TooltipProps<ValueType, NameType>) => JSX.Element | null,
      tooltipContent2: ({
        active,
        payload,
        label,
      }: TooltipProps<ValueType, NameType>) => JSX.Element | null,
      tooltipContent3: ({
        active,
        payload,
        label,
      }: TooltipProps<ValueType, NameType>) => JSX.Element | null;
    switch (chartKey) {
      case 'square_meter_price_over_period':
        formatter = (value: string | number) => `R$ ${value}`;
        tooltipContent1 = ({ active, payload, label }) => {
          if (active && payload && payload.length) {
            return (
              <div className="custom-tooltip">
                <label className="label">
                  <strong>{`${label}`}</strong>
                </label>
                {payload
                  .filter(
                    (e: Payload<ValueType, NameType>) =>
                      !(e?.name as string)?.includes('transacted')
                  )
                  ?.map((e: Payload<ValueType, NameType>, i: number) => {
                    return (
                      <div key={`custom-tooltip-${i}-${chartKey}`}>
                        <p>
                          <strong style={{ color: colors[i] }}>
                            {e.name} m²
                          </strong>
                          :{' '}
                          {currencyMask.format(
                            Number(e.payload[e.name as keyof typeof e.payload])
                          )}
                        </p>
                      </div>
                    );
                  })}
              </div>
            );
          }
          return null;
        };

        return (
          <>
            <div className="chart-box-view">
              <ResponsiveContainer
                data-cy={`RealtyoffersOverExisting${'grafico'}`}
              >
                <LineChart
                  data={buildingData?.[chartKey]}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <XAxis dataKey="period" />
                  <YAxis
                    tickFormatter={(value: string) =>
                      currencyMask.format(Number(value))
                    }
                  />
                  <Legend iconType="circle" formatter={(e) => `${e} m²`} />
                  <Tooltip content={tooltipContent1} />
                  {Object.keys(buildingData?.[chartKey][0]).map(
                    (item: any, i: number) => {
                      if (!item.includes('period')) {
                        return (
                          <Line
                            isAnimationActive={false}
                            type="monotone"
                            dataKey={item}
                            stroke={colors[i]}
                            activeDot={{ r: 8 }}
                            key={`barchart-${item}-${i}-${chartKey}`}
                          />
                        );
                      } else return null;
                    }
                  )}
                  <Brush dataKey="period" height={30} stroke="#8884d8" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        );
      case 'sold_units_over_price':
        tooltipContent2 = ({ active, payload, label }: any) => {
          if (active && payload && payload.length) {
            return (
              <div className="custom-tooltip">
                <label className="label">
                  <strong>{`${label}`}</strong>
                </label>
                {payload
                  .filter((e: any) => !e.name.includes('transacted'))
                  ?.map((e: any, i: number) => {
                    return (
                      <div key={`custom-tooltip-${i}-${chartKey}`}>
                        <p>
                          <strong style={{ color: colors[i] }}>
                            {e.name} m²
                          </strong>
                          : {`${e.payload[e.name]} %`}
                        </p>
                      </div>
                    );
                  })}
              </div>
            );
          }
          return null;
        };
        return (
          <>
            <div className="chart-box-view">
              <ResponsiveContainer
                data-cy={`RealtyoffersOverExisting${'grafico'}`}
              >
                <LineChart
                  data={buildingData?.[chartKey]}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <XAxis dataKey="period" />
                  <YAxis unit="%" />
                  <Legend iconType="circle" formatter={(e) => `${e} m²`} />
                  <Tooltip content={tooltipContent2} />
                  {Object.keys(buildingData?.[chartKey][0]).map(
                    (iten: any, i: number) => {
                      if (!iten.includes('period')) {
                        return (
                          <Line
                            isAnimationActive={false}
                            type="monotone"
                            key={`barchart-${iten}-${i}-${chartKey}`}
                            dataKey={iten}
                            stroke={colors[i]}
                            activeDot={{ r: 8 }}
                          />
                        );
                      } else return null;
                    }
                  )}
                  <Brush dataKey="period" height={30} stroke="#8884d8" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        );
      case 'releases_sold_and_on_stock_over_period':
        tooltipContent3 = ({ active, payload, label }: any) => {
          if (active && payload && payload.length) {
            return (
              <div className="custom-tooltip">
                <label className="label">
                  <strong>{`${label}`}</strong>
                </label>
                <div>
                  <p>
                    <strong style={{ color: payload[1].fill }}>
                      {nls[payload[1].name]}
                    </strong>
                    :
                    <span
                      style={{}}
                    >{`${payload[1].payload.stock} unidades`}</span>
                  </p>
                  <p>
                    <strong style={{ color: payload[0].fill }}>
                      {nls[payload[0].name]}
                    </strong>
                    :
                    <span
                      style={{}}
                    >{`${payload[1].payload.sold} unidades`}</span>
                  </p>
                </div>
              </div>
            );
          }
          return null;
        };
        return (
          <>
            <div className="chart-box-view">
              <ResponsiveContainer
                data-cy={`RealtyoffersOverExisting${'grafico'}`}
              >
                <BarChart data={buildingData?.[chartKey]}>
                  <Tooltip content={tooltipContent3} />
                  <XAxis dataKey="period" />
                  <YAxis type="number" unit="un" />
                  <Legend iconType="circle" formatter={(e) => nls[e]} />
                  <Bar
                    dataKey="sold"
                    stackId="a"
                    fill={colors[2]}
                    label={{ position: 'centerTop', fill: '#FFF' }}
                    isAnimationActive={false}
                  />
                  <Bar
                    dataKey="stock"
                    stackId="a"
                    fill={colors[1]}
                    isAnimationActive={false}
                  >
                    <LabelList
                      dataKey="stock"
                      fill="#000"
                      position="centerTop"
                    />
                  </Bar>
                  <Brush dataKey="period" height={30} stroke="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        );
    }
  };

  const modalHead = () => {
    return (
      <div className="offers-primary-modal-head-container">
        <IconOffersPrimary cy="IconOffersPrimary" />
        <div className="offers-primary-modal-head-info-container">
          <h1>{buildingData?.enterprise_summary?.building_name}</h1>
          <p>{buildingData?.enterprise_summary?.address}</p>
        </div>
      </div>
    );
  };
  return statusModal === `open building` ? (
    <Modal
      cy="BuildingDetails"
      title={modalHead()}
      open={statusModal === `open building`}
      close={() => dispatch(modalOpen(''))}
      size="large-full"
      borderHeader={true}
      actions
    >
      <div className="offers-primary-modal-detail">
        <div className="offers-primary-modal-detail-container">
          <h6 className="offers-primary-header-detail-title">
            {nls.offersPrimaryDetail}
          </h6>
        </div>
        <div className="offers-primary-modal--body-container column4">
          {structureHeader[1].map(
            (esKey: EnterpriseSummaryKeys, index: number) => (
              <p
                key={`${esKey}-1-${index}`}
                className="offers-primary-modal--body-container--key-value-pair"
                data-cy={`BuildingModal${id}BodyInfo${esKey}`}
              >
                {`${nls[esKey]}: `}
                {esKey === 'list_of_garages'
                  ? buildingData?.enterprise_summary['list_of_garages'].map(
                      (lg: number, i: number) => `${i === 0 ? '' : ','} ${lg}`
                    )
                  : esKey === 'vgv'
                  ? `R$${
                      buildingData?.enterprise_summary[
                        esKey as keyof EnterpriseSummary
                      ]
                    }`
                  : buildingData?.enterprise_summary[
                      esKey as keyof EnterpriseSummary
                    ]}
              </p>
            )
          )}
        </div>
      </div>
      <div className="building-modal--table-container">
        <div className="building-modal--table-container--header">
          <h6
            className="header-table-title"
            data-cy={`BuildingModal${id}EnterpriseAnalysis`}
          >
            {nls.panorama}
          </h6>
        </div>
        <div className="building-modal--table-container--body">
          <List
            header={tableHeader}
            data={formattedTableData}
            cy={`BuildingModal${id}AnalysisTable`}
            listCustom={(e) => {
              const itemCustom = e;

              itemCustom['percentage_sold-custom'] = `${e.percentage_sold} %`;
              itemCustom[
                'units_available_per_area-custom'
              ] = `${e.units_available_per_area}`;

              return itemCustom;
            }}
            noData="Sem informação"
          />
        </div>
      </div>
      <div className="building-modal--charts-container offers-primary-charts-container">
        <div className="building-modal--charts-container--header">
          <h6
            className="header-chart-title"
            data-cy={`BuildingModal${id}Charts`}
          >
            {nls.indicators}
          </h6>
        </div>
        <div className="building-modal--charts-container--body">
          {chartListype.map((chartKey: ChartKeys, i: number) => {
            return (
              <div className="chart-box" key={`grafico-${chartKey}`}>
                <label>{nls[chartKey]}</label>
                {graficos(chartKey)}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  ) : (
    <></>
  );
};

export default BuildingDetailsModalOffers;
